import {
  currentLondonYear,
  QUESTIONS_PER_ROUND,
  quipFor,
  tierFor,
  type Sport,
  SPORTS,
} from '@geosport/shared';
import { useEffect, useState } from 'react';
import { BottomSheet } from '../components/BottomSheet';
import { Button } from '../components/Button';
import { ClueCard } from '../components/ClueCard';
import { Drawer } from '../components/Drawer';
import { QuipOverlay } from '../components/QuipOverlay';
import { RevealCard } from '../components/RevealCard';
import { TopBar } from '../components/TopBar';
import { YearSlider } from '../components/YearSlider';
import { track } from '../lib/analytics';
import { getBackend } from '../lib/backend';
import { navigate, usePath, useSearch } from '../lib/router';
import { useReducedMotion, useSettings } from '../lib/settings';
import { Globe, type RevealOverlay } from '../map/Globe';
import { selectCurrentQuestion, selectRunningScore, useRound } from '../store/round';

type Target =
  | { kind: 'daily' }
  | { kind: 'archive'; date: string }
  | { kind: 'random'; sport: Sport | null }
  | { kind: 'special'; slug: string };

function targetFor(path: string, search: URLSearchParams): Target {
  const archive = /^\/play\/archive\/(\d{4}-\d{2}-\d{2})$/.exec(path);
  if (archive) return { kind: 'archive', date: archive[1]! };
  const special = /^\/play\/special\/([a-z0-9-]{3,60})$/.exec(path);
  if (special) return { kind: 'special', slug: special[1]! };
  if (path === '/play/random') {
    const sport = search.get('sport') ?? '';
    return {
      kind: 'random',
      sport: (SPORTS as readonly string[]).includes(sport) ? (sport as Sport) : null,
    };
  }
  return { kind: 'daily' };
}

export function Play() {
  const path = usePath();
  const search = useSearch();
  const target = targetFor(path, search);
  const phase = useRound((s) => s.phase);
  const mode = useRound((s) => s.mode);
  const sport = useRound((s) => s.sport);
  const date = useRound((s) => s.date);
  const puzzleNo = useRound((s) => s.puzzleNo);
  const position = useRound((s) => s.position);
  const pin = useRound((s) => s.pin);
  const year = useRound((s) => s.year);
  const reveals = useRound((s) => s.reveals);
  const error = useRound((s) => s.error);
  const specialTitle = useRound((s) => s.special?.title ?? null);
  const question = useRound(selectCurrentQuestion);
  const score = useRound(selectRunningScore);
  const {
    setPin,
    lockLocation,
    setYear,
    lockYear,
    next,
    start,
    startArchive,
    startRandom,
    startSpecial,
  } = useRound.getState();
  const reducedMotion = useReducedMotion();
  const units = useSettings((s) => s.units);
  const mapStyle = useSettings((s) => s.mapStyle);
  const [menuOpen, setMenuOpen] = useState(false);

  // Route → round. Daily rounds are loaded by App; practice rounds start here, once per route.
  const routeKey = `${path}?${search.toString()}`;
  useEffect(() => {
    const t = targetFor(
      new URL(routeKey, window.location.origin).pathname,
      new URL(routeKey, window.location.origin).searchParams,
    );
    if (t.kind === 'archive') void startArchive(t.date);
    else if (t.kind === 'random') void startRandom(t.sport);
    else if (t.kind === 'special') void startSpecial(t.slug);
  }, [routeKey, startArchive, startRandom, startSpecial]);

  useEffect(() => {
    if (target.kind !== 'daily') {
      if (phase === 'done') navigate('/results', { replace: true });
      return;
    }
    if (mode !== 'daily') {
      void useRound.getState().exitPractice();
      return;
    }
    if (phase === 'idle') start();
    else if (phase === 'done') navigate('/results', { replace: true });
    else if (phase === 'error') navigate('/', { replace: true });
  }, [target.kind, phase, mode, start]);

  const currentReveal = phase === 'reveal' ? reveals[reveals.length - 1] : undefined;
  const overlay: RevealOverlay | null = currentReveal
    ? {
        guess: currentReveal.guess,
        answer: currentReveal.answer,
        tier: tierFor(currentReveal.basePts),
      }
    : null;

  if (phase === 'boot' || phase === 'loading') {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3 text-white/60">
        <div className="skeleton h-3 w-40 rounded-full" />
        <span>Loading round…</span>
      </div>
    );
  }

  if (phase === 'error' && target.kind !== 'daily') {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-white/80">{error}</p>
        <Button variant="secondary" onClick={() => navigate('/practice')}>
          Back to practice
        </Button>
      </div>
    );
  }

  return (
    <div className="relative h-dvh w-full overflow-hidden bg-ink" data-testid="play-screen">
      <Globe
        className="absolute inset-0"
        pin={pin}
        onPick={setPin}
        reveal={overlay}
        interactive={phase === 'where'}
        reducedMotion={reducedMotion}
        mapStyle={mapStyle}
        bottomInset={0.52}
      />

      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 mx-auto w-full max-w-lg">
        <div className="pointer-events-auto">
          <TopBar
            position={position}
            reveals={reveals}
            score={score}
            mode={mode}
            sport={sport}
            date={date}
            puzzleNo={puzzleNo}
            reducedMotion={reducedMotion}
            onMenu={() => setMenuOpen(true)}
            specialTitle={specialTitle}
          />
        </div>
        {question && phase !== 'reveal' && (
          <div className="pointer-events-auto px-3 pt-2">
            <ClueCard question={question} />
          </div>
        )}
      </div>

      {currentReveal && (
        <QuipOverlay
          text={quipFor({
            basePts: currentReveal.basePts,
            yearDiff: currentReveal.yearDiff,
            distanceKm: currentReveal.distanceKm,
            position: currentReveal.position,
          })}
          tier={tierFor(currentReveal.basePts)}
        />
      )}

      <div className="absolute inset-x-0 bottom-0 z-10 mx-auto max-h-[70dvh] w-full max-w-lg overflow-y-auto">
        {phase === 'where' && (
          <BottomSheet>
            <p className="mb-3 text-center text-sm text-white/70">
              {pin
                ? 'Drag the pin to adjust, or tap somewhere else.'
                : 'Tap the globe where it happened.'}
            </p>
            <Button
              className="w-full"
              disabled={!pin}
              onClick={lockLocation}
              data-testid="lock-location"
            >
              Lock location →
            </Button>
          </BottomSheet>
        )}

        {(phase === 'when' || phase === 'submitting') && (
          <BottomSheet>
            <YearSlider value={year} max={currentLondonYear()} onChange={setYear} />
            {error && <p className="mt-2 text-center text-sm text-red-300">{error}</p>}
            <Button
              className="mt-3 w-full"
              disabled={year === null || phase === 'submitting'}
              onClick={() => void lockYear()}
              data-testid="lock-year"
            >
              {phase === 'submitting' ? 'Checking…' : 'Lock it in →'}
            </Button>
          </BottomSheet>
        )}

        {phase === 'reveal' && currentReveal && (
          <BottomSheet>
            <RevealCard
              reveal={currentReveal}
              units={units}
              onReport={async (reason, note) => {
                const token = useRound.getState().progressToken;
                if (!token) return false;
                try {
                  const backend = await getBackend();
                  await backend.report({
                    progressToken: token,
                    position: currentReveal.position,
                    reason,
                    note: note.trim() || undefined,
                  });
                  track('question_reported', { mode, position: currentReveal.position, reason });
                  return true;
                } catch {
                  return false;
                }
              }}
              onNext={() => {
                const last = currentReveal.position === QUESTIONS_PER_ROUND;
                next();
                if (last) navigate('/results');
              }}
            />
          </BottomSheet>
        )}
      </div>

      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} />
    </div>
  );
}
